import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTripContext } from './TripDetailPage';
import { useAiDataVersion, useAiPanel } from '../../components/ai/AiPanelContext';
import { notesApi } from '../../api/notes.api';
import type { NoteDocument, TripNote } from '../../types';
import { toast } from '../../components/ui/Toast';
import { isAlreadyGone } from '../../api/client';
import { tooLargeMessage, uploadErrorMessage } from '../../utils/uploadLimits';
import Spinner from '../../components/ui/Spinner';
import LoadingScreen from '../../components/ui/LoadingScreen';
import ConfirmModal from '../../components/ui/ConfirmModal';
import { BookOpen, FileText, Image, Paperclip, Plus, Trash2, Upload, X, Icon } from '../../components/ui/Icon';
import EmptyState from '../../components/ui/EmptyState';

const R2_PUBLIC_URL = (import.meta.env.VITE_R2_PUBLIC_URL as string | undefined)?.replace(/\/$/, '') ?? '';
function fileUrl(filename: string) { return `${R2_PUBLIC_URL}/${filename}`; }
function fileIcon(mimeType: string) { return mimeType.startsWith('image/') ? Image : FileText; }
function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const SAVE_DELAY = 600;

export default function NotesTab() {
  const { trip } = useTripContext();
  // No specialist here, but gIA is on every tab: it answers with no hint.
  useAiPanel({ context: 'general' });
  const { t } = useTranslation();
  const [notes, setNotes]           = useState<TripNote[]>([]);
  const [selected, setSelected]     = useState<TripNote | null>(null);
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [mobileView, setMobileView] = useState<'list' | 'editor'>('list');
  const [noteToDelete, setNoteToDelete] = useState<TripNote | null>(null);

  const titleRef   = useRef<HTMLInputElement>(null);
  const saveTimer  = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef<{ title: string; content: string } | null>(null);

  // ADR-0028: re-read when an accepted card changed a note; keep the open one open.
  const aiNotesVersion = useAiDataVersion('notes');
  useEffect(() => {
    notesApi.list(trip.id)
      .then(data => {
        setNotes(data);
        setSelected(prev => data.find(n => n.id === prev?.id) ?? data[0] ?? null);
      })
      .catch(() => toast(t('notes.loadError', 'Failed to load notes'), 'error'))
      .finally(() => setLoading(false));
  }, [trip.id, aiNotesVersion]);

  const flushSave = useCallback(async (noteId: string, data: { title: string; content: string }) => {
    setSaving(true);
    try {
      const updated = await notesApi.update(trip.id, noteId, data);
      setNotes(prev => prev.map(n => n.id === noteId ? updated : n));
      setSelected(prev => prev?.id === noteId ? updated : prev);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1500);
    } catch {
      toast(t('notes.saveError', 'Failed to save note'), 'error');
    } finally {
      setSaving(false);
    }
  }, [trip.id, t]);

  function scheduleAutoSave(noteId: string, title: string, content: string) {
    pendingRef.current = { title, content };
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      if (pendingRef.current) {
        flushSave(noteId, pendingRef.current);
        pendingRef.current = null;
      }
    }, SAVE_DELAY);
  }

  function handleFieldChange(field: 'title' | 'content', value: string) {
    if (!selected) return;
    const updated = { ...selected, [field]: value };
    setSelected(updated);
    setNotes(prev => prev.map(n => n.id === selected.id ? updated : n));
    scheduleAutoSave(selected.id, updated.title, updated.content);
  }

  async function handleCreate() {
    try {
      const note = await notesApi.create(trip.id, { title: '', content: '' });
      setNotes(prev => [note, ...prev]);
      setSelected(note);
      setMobileView('editor');
      setTimeout(() => titleRef.current?.focus(), 50);
    } catch {
      toast(t('notes.createError', 'Failed to create note'), 'error');
    }
  }

  async function handleDelete(note: TripNote) {
    try {
      await notesApi.delete(trip.id, note.id);
      const remaining = notes.filter(n => n.id !== note.id);
      setNotes(remaining);
      if (selected?.id === note.id) {
        setSelected(remaining[0] ?? null);
        setMobileView('list');
      }
    } catch {
      toast(t('notes.deleteError', 'Failed to delete note'), 'error');
    }
  }

  function handleDocsChange(noteId: string, docs: NoteDocument[]) {
    const updated = (n: TripNote) => n.id === noteId ? { ...n, documents: docs } : n;
    setNotes(prev => prev.map(updated));
    setSelected(prev => prev?.id === noteId ? { ...prev, documents: docs } : prev);
  }

  function selectNote(note: TripNote) {
    if (saveTimer.current && pendingRef.current && selected) {
      clearTimeout(saveTimer.current);
      flushSave(selected.id, pendingRef.current);
      pendingRef.current = null;
    }
    setSelected(note);
    setMobileView('editor');
  }

  if (loading) return <LoadingScreen />;

  const noteTitle = (note: TripNote) => note.title.trim() || t('notes.untitled', 'Untitled');
  const notePreview = (note: TripNote) => note.content.split('\n')[0].trim() || t('notes.empty', 'No content');

  return (
    <>
    <div className="flex flex-col h-full overflow-hidden animate-fade-in">
      {/* ── Desktop: 2-column layout ─────────────────────────── */}
      <div className="hidden md:flex h-full min-h-0">
        {/* Left: note list */}
        <div className="w-64 shrink-0 flex flex-col border-r border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
          <div className="flex items-center justify-between px-4 py-4 border-b border-slate-100 dark:border-slate-800">
            <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200">
              {t('trip.nav.notes', 'Notes')}
            </h2>
            <button
              onClick={handleCreate}
              className="flex items-center gap-1 text-xs font-medium text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 transition-colors cursor-pointer"
            >
              <Icon as={Plus} size={14} />
              {t('notes.new', 'New')}
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            {notes.length === 0 ? (
              <EmptyState size="inline" icon={BookOpen} title={t('notes.emptyState')} />
            ) : (
              notes.map(note => (
                <button
                  key={note.id}
                  onClick={() => selectNote(note)}
                  className={`w-full text-left px-3 py-2.5 rounded-xl transition-colors cursor-pointer ${
                    selected?.id === note.id ? 'bg-teal-50 dark:bg-teal-900/20' : 'hover:bg-slate-50 dark:hover:bg-slate-800'
                  }`}
                >
                  <p className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">{noteTitle(note)}</p>
                  <p className="text-xs text-slate-400 dark:text-slate-500 truncate mt-0.5">{notePreview(note)}</p>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Right: editor */}
        <div className="flex-1 flex flex-col min-w-0 bg-white dark:bg-slate-900">
          {selected ? (
            <NoteEditor
              note={selected}
              tripId={trip.id}
              titleRef={titleRef}
              saving={saving}
              savedFlash={savedFlash}
              onTitleChange={v => handleFieldChange('title', v)}
              onContentChange={v => handleFieldChange('content', v)}
              onDelete={() => setNoteToDelete(selected)}
              onDocsChange={docs => handleDocsChange(selected.id, docs)}
            />
          ) : (
            <EmptyState
              className="h-full"
              icon={BookOpen}
              title={notes.length === 0 ? t('notes.emptyState') : t('notes.selectOrCreate')}
              action={{
                label: notes.length === 0 ? t('notes.createFirst') : t('notes.new'),
                icon: Plus,
                onClick: handleCreate,
              }}
            />
          )}
        </div>
      </div>

      {/* ── Mobile layout ────────────────────────────────────── */}
      <div className="md:hidden flex flex-col h-full min-h-0">
        {mobileView === 'list' ? (
          <>
            <div className="flex items-center justify-between px-4 py-4 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 shrink-0">
              <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                {t('trip.nav.notes', 'Notes')}
              </h2>
              <button
                onClick={handleCreate}
                className="flex items-center gap-1 text-xs font-medium text-teal-600 dark:text-teal-400 cursor-pointer"
              >
                <Icon as={Plus} size={14} />
                {t('notes.new', 'New')}
              </button>
            </div>
            <div className="flex-1 overflow-y-auto bg-slate-50 dark:bg-slate-950">
              {notes.length === 0 ? (
                <EmptyState
                  className="h-full"
                  icon={BookOpen}
                  title={t('notes.emptyState')}
                  action={{ label: t('notes.createFirst'), icon: Plus, onClick: handleCreate }}
                />
              ) : (
                <div className="p-3 space-y-2">
                  {notes.map(note => (
                    <button
                      key={note.id}
                      onClick={() => selectNote(note)}
                      className="w-full text-left px-4 py-3.5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                    >
                      <p className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">{noteTitle(note)}</p>
                      <p className="text-xs text-slate-400 dark:text-slate-500 truncate mt-0.5">{notePreview(note)}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="flex flex-col h-full min-h-0 bg-white dark:bg-slate-900">
            <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200 dark:border-slate-800 shrink-0">
              <button
                onClick={() => setMobileView('list')}
                className="text-xs font-medium text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 cursor-pointer"
              >
                ← {t('notes.allNotes', 'All notes')}
              </button>
              {saving && <Spinner size="sm" />}
              {savedFlash && !saving && (
                <span className="text-xs text-teal-600 dark:text-teal-400 ml-auto">{t('notes.saved', 'Saved')}</span>
              )}
            </div>
            {selected && (
              <NoteEditor
                note={selected}
                tripId={trip.id}
                titleRef={titleRef}
                saving={saving}
                savedFlash={savedFlash}
                hideSaveIndicator
                onTitleChange={v => handleFieldChange('title', v)}
                onContentChange={v => handleFieldChange('content', v)}
                onDelete={() => setNoteToDelete(selected)}
                onDocsChange={docs => handleDocsChange(selected.id, docs)}
              />
            )}
          </div>
        )}
      </div>
    </div>
    <ConfirmModal
      open={!!noteToDelete}
      title={t('notes.deleteConfirm', 'Delete this note?')}
      body={t('itinerary.delete.body')}
      onConfirm={async () => { if (noteToDelete) { await handleDelete(noteToDelete); setNoteToDelete(null); } }}
      onCancel={() => setNoteToDelete(null)}
    />
    </>
  );
}

interface NoteEditorProps {
  note: TripNote;
  tripId: string;
  titleRef: React.RefObject<HTMLInputElement | null>;
  saving: boolean;
  savedFlash: boolean;
  hideSaveIndicator?: boolean;
  onTitleChange: (v: string) => void;
  onContentChange: (v: string) => void;
  onDelete: () => void;
  onDocsChange: (docs: NoteDocument[]) => void;
}

function NoteEditor({ note, tripId, titleRef, saving, savedFlash, hideSaveIndicator, onTitleChange, onContentChange, onDelete, onDocsChange }: NoteEditorProps) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex items-center justify-between px-6 py-3 border-b border-slate-100 dark:border-slate-800 shrink-0">
        {!hideSaveIndicator && (
          <div className="flex items-center gap-2 h-5">
            {saving && <Spinner size="sm" />}
            {savedFlash && !saving && (
              <span className="text-xs text-teal-600 dark:text-teal-400">{t('notes.saved', 'Saved')}</span>
            )}
          </div>
        )}
        <div className="flex-1" />
        <button
          onClick={onDelete}
          className="p-1.5 rounded-md text-slate-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors cursor-pointer"
          title={t('notes.delete', 'Delete note')}
        >
          <Icon as={Trash2} size={15} />
        </button>
      </div>
      {/* Title and body are separate fields with no chrome of their own, so a
          filled note reads as one block of text. The labels + divider are what
          tell them apart. */}
      <div className="shrink-0 px-6 pt-4 pb-3 border-b border-slate-100 dark:border-slate-800">
        <label htmlFor="note-title" className="block font-mono text-[10px] tracking-[0.14em] uppercase text-slate-400 dark:text-slate-500 mb-1">
          {t('notes.titleLabel', 'Title')}
        </label>
        <input
          id="note-title"
          ref={titleRef}
          value={note.title}
          onChange={e => onTitleChange(e.target.value)}
          placeholder={t('notes.titlePlaceholder', 'Note title')}
          className="w-full text-xl font-semibold text-slate-900 dark:text-slate-100 bg-transparent border-none outline-none placeholder:text-slate-300 dark:placeholder:text-slate-600"
        />
      </div>
      {/* The textarea itself takes the leftover height and scrolls: a fixed
          height left a dead white gap under a clipped note. */}
      <div className="flex-1 flex flex-col min-h-0 px-6 pt-3 pb-4">
        <label htmlFor="note-body" className="block font-mono text-[10px] tracking-[0.14em] uppercase text-slate-400 dark:text-slate-500 mb-1.5">
          {t('notes.contentLabel', 'Content')}
        </label>
        <textarea
          id="note-body"
          value={note.content}
          onChange={e => onContentChange(e.target.value)}
          placeholder={t('notes.contentPlaceholder', 'Write your notes here…')}
          className="flex-1 min-h-0 w-full overflow-y-auto text-sm text-slate-700 dark:text-slate-300 bg-transparent border-none outline-none resize-none placeholder:text-slate-300 dark:placeholder:text-slate-600 leading-relaxed"
        />
      </div>
      <NoteDocuments tripId={tripId} note={note} onDocsChange={onDocsChange} />
    </div>
  );
}

function NoteDocuments({ tripId, note, onDocsChange }: {
  tripId: string;
  note: TripNote;
  onDocsChange: (docs: NoteDocument[]) => void;
}) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [docToDelete, setDocToDelete] = useState<NoteDocument | null>(null);
  const docs: NoteDocument[] = note.documents ?? [];

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    setUploading(true);
    // One rejected file doesn't cancel the rest, and whatever did go up is kept:
    // dropping it would leave objects in R2 that the note never lists.
    const uploaded: NoteDocument[] = [];
    for (const file of Array.from(files)) {
      const tooLarge = tooLargeMessage(file, 'document');
      if (tooLarge) {
        toast(tooLarge, 'error');
        continue;
      }
      try {
        uploaded.push(await notesApi.uploadDocument(tripId, note.id, file));
      } catch (e) {
        toast(uploadErrorMessage(e, file.name, 'document', t('notes.uploadError')), 'error');
      }
    }
    if (uploaded.length > 0) onDocsChange([...docs, ...uploaded]);
    setUploading(false);
    if (inputRef.current) inputRef.current.value = '';
  }

  async function handleDeleteDoc() {
    if (!docToDelete) return;
    try {
      await notesApi.deleteDocument(tripId, note.id, docToDelete.id);
    } catch (e) {
      if (!isAlreadyGone(e)) {
        toast(t('notes.deleteDocError', 'Failed to delete file'), 'error');
        setDocToDelete(null);
        return;
      }
    }
    onDocsChange(docs.filter(d => d.id !== docToDelete.id));
    setDocToDelete(null);
  }

  return (
    <div className="shrink-0 mx-4 mb-4 rounded-2xl bg-slate-50/80 dark:bg-slate-800/40 px-4 py-3">
      <div className="flex items-center gap-2 mb-2">
        <Icon as={Paperclip} size={13} className="text-slate-400 dark:text-slate-500" />
        <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">
          {t('notes.attachments', 'Attachments')}
        </span>
        {docs.length > 0 && (
          <span className="px-1.5 py-0.5 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400 text-[10px] font-bold">
            {docs.length}
          </span>
        )}
        <button
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="ml-auto flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:border-teal-300 dark:hover:border-teal-600 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-colors cursor-pointer disabled:opacity-40 shadow-sm"
        >
          {uploading ? <Spinner size="sm" /> : <Icon as={Upload} size={12} />}
          {t('notes.upload', 'Upload')}
        </button>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="image/*,application/pdf"
          className="hidden"
          onChange={e => handleFiles(e.target.files)}
        />
      </div>
      {docs.length === 0 ? (
        <button
          onClick={() => inputRef.current?.click()}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 border-dashed border-slate-200 dark:border-slate-700 text-xs text-slate-400 dark:text-slate-500 hover:border-teal-300 dark:hover:border-teal-600 hover:text-teal-500 hover:bg-teal-50/50 dark:hover:bg-teal-900/20 transition-colors cursor-pointer"
        >
          <Icon as={Upload} size={13} />
          {t('notes.dropHere', 'Click to attach files (images or PDFs)')}
        </button>
      ) : (
        <div className="flex flex-col gap-1">
          {docs.map(doc => (
            <div key={doc.id} className="flex items-center gap-2 px-2.5 py-2 rounded-xl hover:bg-white dark:hover:bg-slate-800 group transition-colors">
              <Icon as={fileIcon(doc.mimeType)} size={13} className="text-slate-400 dark:text-slate-500 shrink-0" />
              <a
                href={fileUrl(doc.filename)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 min-w-0 text-xs text-teal-600 hover:text-teal-700 hover:underline truncate font-medium"
              >
                {doc.originalName}
              </a>
              <span className="text-[10px] text-slate-400 dark:text-slate-500 shrink-0">{formatBytes(doc.size)}</span>
              <button
                onClick={() => setDocToDelete(doc)}
                className="w-5 h-5 rounded flex items-center justify-center text-slate-300 dark:text-slate-600 hover:text-red-400 cursor-pointer md:opacity-0 md:group-hover:opacity-100 transition-opacity"
              >
                <Icon as={X} size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
      <ConfirmModal
        open={!!docToDelete}
        title={t('bookings.docs.deleteTitle')}
        body={docToDelete ? t('bookings.docs.deleteConfirm', { name: docToDelete.originalName }) : undefined}
        onConfirm={handleDeleteDoc}
        onCancel={() => setDocToDelete(null)}
      />
    </div>
  );
}
